import React, { useState, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import CustomModal from "../../components/common/general/CustomModal/CustomModal";
import InteractiveLearningForm from "../../components/customCoursework/ActivityForms/InteractiveLearningForm/InteractiveLearningForm";
import ResourceForm from "../../components/customCoursework/ActivityForms/ResourceForm/ResourceForm";
import AssignmentForm from "../../components/customCoursework/ActivityForms/AssignmentForm/AssignmentForm";
import ProjectForm from "../../components/customCoursework/ActivityForms/ProjectForm/ProjectForm";
import ChooseActivity from "../../components/customCoursework/ChooseActivity/ChooseActivity";

const ActivityFormContainer = ({ visible, onClose, onSave, error, isSubmitting, existingActivity = null }) => {
  // Get activity type from existing activity or default to null
  const getActivityType = () => {
    if (!existingActivity) return null;

    if (existingActivity.type) {
      return existingActivity.type;
    } else if (existingActivity.__typename === "AITutor") {
      return "Interactive Learning Experience";
    } else if (existingActivity.__typename === "AxioResource") {
      return "Resource";
    } else if (existingActivity.__typename === "Assignment") {
      return "Assignment";
    } else if (existingActivity.__typename === "Project") {
      return "Project";
    }

    console.warn("Could not determine activity type, defaulting to Interactive Learning Experience");
    return "Interactive Learning Experience";
  };

  const [step, setStep] = useState(existingActivity ? "edit" : "choose");
  const [selectedActivityType, setSelectedActivityType] = useState(getActivityType());
  const [formError, setFormError] = useState(error || "");

  // Important: Reset the state when the visibility changes
  useEffect(() => {
    if (visible) {
      // When modal becomes visible, set the initial state based on existingActivity
      setStep(existingActivity ? "edit" : "choose");
      setSelectedActivityType(getActivityType());
      setFormError("");
    }
  }, [visible, existingActivity]);

  // Also reset when existingActivity changes
  useEffect(() => {
    // Reset to choose step if we have no existing activity (new activity)
    if (!existingActivity) {
      setStep("choose");
      setSelectedActivityType(null);
    } else {
      setStep("edit");
      setSelectedActivityType(getActivityType());
    }
  }, [existingActivity]);

  const handleActivityTypeSelect = (type) => {
    setSelectedActivityType(type);
    setStep("edit");
  };

  const handleFormSubmit = async (formData) => {
    try {
      setFormError("");
      await onSave({
        ...formData,
        type: selectedActivityType,
        id: existingActivity?.id,
      });

      // Reset form state after successful save
      if (!existingActivity) {
        // Only reset if this was a new activity (not editing)
        setStep("choose");
        setSelectedActivityType(null);
      }
    } catch (err) {
      setFormError(err.message || "Failed to save activity");
      throw err;
    }
  };

  const handleClose = () => {
    // Reset state before closing
    onClose();
  };

  const renderForm = () => {
    // Common props for all form components
    const formProps = {
      activityData: existingActivity,
      onSave: handleFormSubmit,
      onCancel: handleClose,
      error: formError,
      isSubmitting,
      isNewActivity: !existingActivity?.id,
    };

    switch (selectedActivityType) {
      case "Interactive Learning Experience":
        return <InteractiveLearningForm {...formProps} />;
      case "Resource":
        return <ResourceForm {...formProps} />;
      case "Assignment":
        return <AssignmentForm {...formProps} />;
      case "Project":
        return <ProjectForm {...formProps} />;
      default:
        return null;
    }
  };

  return (
    <CustomModal visible={visible} onRequestClose={handleClose} showCloseButton={false} style={styles.modal}>
      <View style={styles.container} id="activity-form-container">
        {!existingActivity && step === "choose" ? (
          <ChooseActivity onSelect={handleActivityTypeSelect} onCancel={handleClose} />
        ) : (
          renderForm()
        )}
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modal: {
    margin: 10,
    maxHeight: "90vh",
    width: "90vw",
    maxWidth: 1000,
    padding: 0,
  },
  container: {
    flex: 1,
    backgroundColor: "#fff",
    borderRadius: 8,
    height: "100%",
    overflow: "hidden",
  },
});

export default ActivityFormContainer;
